import type { PortalAccessibleBook } from '@/lib/books-api';

const PORTAL_SESSION_KEY = 'flipbook_portal_access';
const BOOK_ACCESS_GRANTS_KEY = 'flipbook_book_access_grants';
const BOOK_ACCESS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type PortalAccessSession = {
  email: string;
  portal_token: string;
  access_link?: string;
  books: PortalAccessibleBook[];
  verified_at: string;
};

export type BookAccessGrant = {
  book_ref: string;
  book_id?: number;
  email: string;
  access_token: string | null;
  otp_required: boolean;
  verified_at: string;
  expires_at: string;
};

export function savePortalAccessSession(session: PortalAccessSession) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PORTAL_SESSION_KEY, JSON.stringify(session));
}

export function savePortalSessionFromApi(data: {
  email?: string;
  portal_token?: string;
  access_link?: string;
  books?: PortalAccessibleBook[];
}): PortalAccessSession {
  const books = data.books || [];
  if (!books.length || !data.portal_token || !data.email) {
    throw new Error('Invalid portal access session');
  }

  const session: PortalAccessSession = {
    email: data.email,
    portal_token: data.portal_token,
    access_link: data.access_link,
    books,
    verified_at: new Date().toISOString(),
  };
  savePortalAccessSession(session);

  // Mirror per-book tokens so /read refresh works even without reopening library
  for (const book of books) {
    const ref = book.public_id || String(book.id);
    saveBookAccessGrant({
      book_ref: ref,
      book_id: book.id,
      email: data.email,
      access_token: book.access_token || null,
      otp_required: Boolean(book.otp_required),
    });
  }

  return session;
}

export function loadPortalAccessSession(): PortalAccessSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PORTAL_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PortalAccessSession;
    if (!parsed?.email || !Array.isArray(parsed.books)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getStoredPortalToken(): string | null {
  const session = loadPortalAccessSession();
  return session?.portal_token || null;
}

export function clearPortalAccessSession() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(PORTAL_SESSION_KEY);
}

function loadBookAccessGrants(): Record<string, BookAccessGrant> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(BOOK_ACCESS_GRANTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, BookAccessGrant>;
    if (!parsed || typeof parsed !== 'object') return {};

    const now = Date.now();
    const next: Record<string, BookAccessGrant> = {};
    for (const [key, grant] of Object.entries(parsed)) {
      if (!grant?.email || !grant?.expires_at) continue;
      if (new Date(grant.expires_at).getTime() <= now) continue;
      next[key] = grant;
    }
    if (Object.keys(next).length !== Object.keys(parsed).length) {
      localStorage.setItem(BOOK_ACCESS_GRANTS_KEY, JSON.stringify(next));
    }
    return next;
  } catch {
    return {};
  }
}

function grantStorageKey(bookRef: string) {
  return String(bookRef || '').trim();
}

export function saveBookAccessGrant(input: {
  book_ref: string;
  book_id?: number;
  email: string;
  access_token?: string | null;
  otp_required: boolean;
  ttlMs?: number;
}) {
  if (typeof window === 'undefined') return;
  const key = grantStorageKey(input.book_ref);
  if (!key || !input.email) return;

  const grants = loadBookAccessGrants();
  const now = Date.now();
  const ttl = input.ttlMs ?? BOOK_ACCESS_TTL_MS;
  grants[key] = {
    book_ref: key,
    book_id: input.book_id,
    email: input.email.trim().toLowerCase(),
    access_token: input.access_token || null,
    otp_required: Boolean(input.otp_required),
    verified_at: new Date(now).toISOString(),
    expires_at: new Date(now + ttl).toISOString(),
  };
  localStorage.setItem(BOOK_ACCESS_GRANTS_KEY, JSON.stringify(grants));
}

export function getBookAccessGrant(bookRef: string): BookAccessGrant | null {
  const key = grantStorageKey(bookRef);
  if (!key) return null;
  const grants = loadBookAccessGrants();
  return grants[key] || null;
}

export function clearBookAccessGrant(bookRef: string) {
  if (typeof window === 'undefined') return;
  const key = grantStorageKey(bookRef);
  const grants = loadBookAccessGrants();
  if (!grants[key]) return;
  delete grants[key];
  localStorage.setItem(BOOK_ACCESS_GRANTS_KEY, JSON.stringify(grants));
}

export function getPortalBookAccess(
  bookRef: string
): { email: string; access_token: string; book: PortalAccessibleBook } | null {
  const session = loadPortalAccessSession();
  if (!session) return null;

  const book = session.books.find(
    (item) => item.public_id === bookRef || String(item.id) === bookRef
  );

  if (!book?.access_token) return null;

  return {
    email: session.email,
    access_token: book.access_token,
    book,
  };
}

/** Prefer portal session, fall back to 7-day per-book grant from /read OTP. */
export function getStoredBookAccess(bookRef: string): {
  email: string;
  access_token: string | null;
  otp_required?: boolean;
  source: 'portal' | 'book_grant';
} | null {
  const portal = getPortalBookAccess(bookRef);
  if (portal) {
    return {
      email: portal.email,
      access_token: portal.access_token,
      otp_required: portal.book.otp_required,
      source: 'portal',
    };
  }

  const grant = getBookAccessGrant(bookRef);
  if (!grant) return null;

  return {
    email: grant.email,
    access_token: grant.access_token,
    otp_required: grant.otp_required,
    source: 'book_grant',
  };
}

export function libraryPathWithToken(token: string) {
  return `/library?token=${encodeURIComponent(token)}`;
}
